import { useEffect, useMemo, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getStripe } from "@/lib/stripe";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TIER_LABEL = { BRONZE: "Bronze", SILVER: "Silver", GOLD: "Gold" };

/**
 * What the merchant is actually paying for (spec 8.1.12).
 *
 * One payment is correct — the add-ons are funded separately from the campaign budget
 * but charged together. The modal showed a single figure, so a merchant could see the
 * total and had no way to check it, or to notice that a Discovery Boost they had toggled
 * on was in it.
 *
 * The lines come from the server's `breakdown`, because the server is what prices them.
 * The frontend used to sum the parts itself, which meant two places could disagree about
 * the charge and only one of them was authoritative.
 */
function ChargeBreakdown({ breakdown }) {
  if (!breakdown) return null;
  const lines = [
    [`${TIER_LABEL[breakdown.tier] || breakdown.tier || "Campaign"} campaign`, breakdown.tier_cost],
    ["Sponsored Trips", breakdown.sponsored_trips],
    ["Discovery Boost", breakdown.discovery_boost],
  ].filter(([, v]) => Number(v || 0) > 0);

  if (!lines.length) return null;

  return (
    <div className="space-y-1 border-t pt-2 mt-2">
      {lines.map(([label, value]) => (
        <div key={label} className="flex justify-between text-xs">
          <span className="text-muted-foreground">{label}</span>
          <span className="tabular-nums">{money(value)}</span>
        </div>
      ))}
      <div className="flex justify-between text-xs font-medium border-t pt-1">
        <span>Total</span>
        <span className="tabular-nums">{money(breakdown.total)}</span>
      </div>
    </div>
  );
}

function PaymentForm({ amount, campaignName, breakdown, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (confirmError) {
      setError(confirmError.message || "Payment failed. Please try again.");
      setSubmitting(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      toast.success("Payment confirmed");
      onSuccess();
    } else if (paymentIntent) {
      // Intents that resolve async (bank debits, etc.) won't be immediately succeeded.
      // Webhook will promote the campaign when it completes.
      toast.info("Payment submitted - campaign will activate once confirmed by your bank.");
      onSuccess();
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-md bg-muted px-3 py-2 text-sm">
        <div className="font-medium">{campaignName}</div>
        <div className="text-2xl font-semibold">{money(amount)}</div>
        <div className="text-muted-foreground text-xs">One-time prepayment for campaign duration</div>
        <ChargeBreakdown breakdown={breakdown} />
      </div>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <div className="text-sm text-destructive">{error}</div>}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" disabled={!stripe || submitting}>
          {submitting ? "Processing…" : `Pay ${money(amount)}`}
        </Button>
      </div>
    </form>
  );
}

export function CampaignPaymentModal({ open, onOpenChange, clientSecret, amount, campaignName, breakdown, token, onSuccess }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!open || !token) return;
    setLoadError(null);
    getStripe(token)
      .then(setStripePromise)
      .catch((err) => {
        console.error("Failed to load Stripe:", err);
        setLoadError("Could not load payment form. Please try again.");
      });
  }, [open, token]);

  const options = useMemo(() => (clientSecret ? { clientSecret, appearance: { theme: "stripe" } } : null), [clientSecret]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Complete payment</DialogTitle>
          <DialogDescription>
            Payment is processed securely by Stripe. We never see or store your card details.
          </DialogDescription>
        </DialogHeader>
        {loadError && <div className="text-sm text-destructive">{loadError}</div>}
        {!loadError && stripePromise && options && (
          <Elements stripe={stripePromise} options={options}>
            <PaymentForm amount={amount} campaignName={campaignName} breakdown={breakdown} onSuccess={onSuccess} />
          </Elements>
        )}
        {!loadError && !stripePromise && <div className="text-sm text-muted-foreground">Loading payment form…</div>}
      </DialogContent>
    </Dialog>
  );
}

export default CampaignPaymentModal;
